import React, { useEffect, useRef, useState } from "react";
import api from "../../api";
import { Alert, Button, Col, Input, InputRef, Row, Skeleton } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { IMessageResponse } from "./types";
import { uuid } from "../../utils/common";
import Message from "./Message";
import { createUseStyles } from "react-jss";

const useStyles = createUseStyles({
  messageContainer: {
    height: "calc(100vh - 200px)",
    overflow: "auto",
    margin: "10px 0",
    padding: "0 15px",
  },
  preFilledQuestions: {
    margin: "5px 0",
  },
});

const END_SESSION = "End Session";

const preFilledQuestions = [
  "Check package status",
  "Create a support ticket",
  "Get support ticket status",
  END_SESSION,
];

const Chat: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [messages, setMessages] = useState<IMessageResponse[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [input, setInput] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const initCalledRef = useRef(false);

  const classes = useStyles();

  const sendMessage = async (msg: string) => {
    try {
      if (errorMessage) {
        setErrorMessage("");
      }
      setLoading(true);
      if (msg) {
        setMessages(msgs =>
          msgs.concat({
            sid: sessionId,
            id: uuid(),
            timestamp: new Date().toISOString(),
            role: "user",
            content: { type: "richText", data: msg },
          })
        );
      }
      const url = sessionId ? `/chat/${sessionId}/` : `/chat/`;

      const { data } = await api.post(url, {
        message: msg,
      });
      setInput("");
      setMessages(msgs => msgs.concat({ ...data }));
      if (!sessionId) {
        setSessionId(data.sid);
      }
    } catch (e) {
      setErrorMessage(
        "Unable to get the response at this moment. Please try again later"
      );
    } finally {
      setLoading(false);
    }
  };

  const onMessageSent = () => {
    if (!input.trim()) return;
    sendMessage(input);
  };

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 500);
    }
  }, [loading]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // const init = () => {
  //   sendMessage("");
  // };
  const init = async () => {
    try {
      const { data } = await api.post("/chat/");
      setMessages([data]);
      setSessionId(data.sid);
    } catch (e) {
      setErrorMessage("Failed to initialize chat");
    }
  };

  const onPreFilledQuestionClick = async (msg: string) => {
    if (msg === END_SESSION) {
      try {
        await api.delete("/chat/");
      } catch (e) {
        console.error("Failed to catch e", e);
      } finally {
        initCalledRef.current = false;
        setSessionId("");
        setMessages([]);
      }
    } else {
      sendMessage(msg);
    }
  };

  useEffect(() => {
    if (!sessionId && !initCalledRef.current) {
      initCalledRef.current = true;
      init();
    }
  }, [sessionId]);

  return (
    <Row justify="center">
      <Col xl={18} lg={18} md={20} sm={24}>
        <div className={classes.messageContainer}>
          {messages.map(d => {
            return <Message key={d.id} data={d} />;
          })}
          {loading ? <Skeleton active /> : null}
          <div ref={chatEndRef} />
        </div>
        {errorMessage ? (
          <Alert message={errorMessage} type="error" showIcon />
        ) : null}
        <Row className={classes.preFilledQuestions} gutter={10}>
          {preFilledQuestions.map((text, i) => (
            <Col key={i}>
              <Button onClick={() => onPreFilledQuestionClick(text)}>
                {text}
              </Button>
            </Col>
          ))}
        </Row>
        <Input
          ref={inputRef}
          disabled={loading}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type your message..."
          onPressEnter={onMessageSent}
          suffix={
            <Button
              size="small"
              loading={loading}
              disabled={loading}
              type="text"
              icon={<SendOutlined />}
              onClick={onMessageSent}
            />
          }
        />
      </Col>
    </Row>
  );
};

export default Chat;
